import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Handshake, Mail, Phone } from "lucide-react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 16) + " UTC"; }
  catch { return iso; }
}

export const AffiliatePanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setData((await api.get("/admin/affiliate-applications")).data); }
    catch { setError("Couldn't load affiliate applications. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!refreshing) load(); }, [refreshing, load]);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await api.get("/admin/affiliate-applications/export", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ethos-affiliate-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { console.error("CSV download failed", e); }
    finally { setDownloading(false); }
  };

  return (
    <section className="card-soft rounded-2xl overflow-hidden mb-8" data-testid="affiliate-panel">
      <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5 border-b border-border">
        <div>
          <span className="eyebrow flex items-center gap-2"><Handshake size={14} /> Partner program</span>
          <h2 className="font-display font-bold text-lg mt-2" data-testid="affiliate-panel-heading">Affiliate applications</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="affiliate-panel-count">
            {loading ? "Loading applications…" : error ? "Applications unavailable" : `${data.total} application${data.total === 1 ? "" : "s"} submitted`}
          </p>
        </div>
        <Button onClick={download} disabled={loading || downloading || !!error} data-testid="download-affiliate-csv-button" className="rounded-xl h-11 px-5 shrink-0">
          {downloading ? <Loader2 className="animate-spin" /> : <Download />}
          {downloading ? "Preparing CSV…" : "Download CSV"}
        </Button>
      </div>

      {error ? (
        <div className="p-6" role="alert" data-testid="affiliate-panel-error">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={load} className="mt-3" data-testid="affiliate-panel-retry">Try again</Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="affiliate-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-[#f6f1e6]">
              <th className="px-6 py-3 font-bold">Applicant</th>
              <th className="px-6 py-3 font-bold hidden sm:table-cell">Audience size</th>
              <th className="px-6 py-3 font-bold hidden md:table-cell">Submitted (UTC)</th>
              <th className="px-6 py-3"></th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-6 py-8 text-muted-foreground" data-testid="affiliate-loading">Loading applications…</td></tr>
              ) : !data.recent.length ? (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground" data-testid="affiliate-empty">No affiliate applications yet. New submissions will appear here.</td></tr>
              ) : (
                data.recent.map((row) => (
                  <tr key={row.id} data-testid={`affiliate-row-${row.id}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors">
                    <td className="px-6 py-4"><p className="font-semibold">{row.name}</p><p className="text-xs text-muted-foreground break-all">{row.email}</p></td>
                    <td className="px-6 py-4 hidden sm:table-cell text-muted-foreground">{row.audience_size || "—"}</td>
                    <td className="px-6 py-4 hidden md:table-cell text-muted-foreground whitespace-nowrap">{fmt(row.created_at)}</td>
                    <td className="px-6 py-4 text-right"><button onClick={() => setSelected(row)} data-testid={`view-affiliate-${row.id}`} className="font-semibold text-xs hover:underline" style={{ color: "var(--gold)" }}>View</button></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && data.total > data.recent.length && <p className="px-6 py-3 text-xs text-muted-foreground">Showing the {data.recent.length} most recent. Download CSV for the complete list.</p>}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#f4f1e9] border-l border-border overflow-y-auto" data-testid="affiliate-detail-drawer">
          <SheetHeader><SheetTitle className="font-display font-bold">Affiliate application</SheetTitle><SheetDescription className="text-xs text-muted-foreground">Full details of the submitted partner application.</SheetDescription></SheetHeader>
          {selected && (
            <div className="mt-6 space-y-5">
              <div className="card-soft rounded-xl p-4">
                <p className="font-semibold text-lg">{selected.name}</p>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 shrink-0" /> {selected.email}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 shrink-0" /> {selected.phone || "—"}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Submitted {fmt(selected.created_at)}</p>
              </div>
              {[
                ["How did you hear about us?", selected.hear_about],
                ["Audience", selected.audience],
                ["Audience size", selected.audience_size],
                ["Links", selected.links],
                ["Promotion plan", selected.promotion_plan],
              ].map(([label, value], i) => (
                <div key={i}>
                  <p className="eyebrow mb-1.5">{label}</p>
                  <p className="text-sm whitespace-pre-wrap break-words card-soft rounded-xl p-3 text-foreground/90">{value || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};
